import React from "react";
import { FileText, ShieldAlert, Copyright, Scale, Calculator, Gavel, Mail, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function TermsOfUse() {
  const sections = [{
    id: "acceptance-usage",
    title: "1. Site Usage and Visitor Conduct",
    icon: CheckCircle2,
    content: <>
          <p className="text-muted-foreground leading-relaxed">
            By accessing or using the website of Brookhaven Elevation Renovations, you agree to comply with and be bound by these Terms of Use. This website is provided solely for informational purposes and to allow prospective clients to learn about our residential renovation services and request project estimates.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            Visitors agree not to use this site for any unlawful purpose, to transmit malicious software, or to interfere with the proper functioning or security of the website. Any unauthorized attempts to scrape data, breach system integrity, or collect private communications are strictly prohibited.
          </p>
        </>
  }, {
    id: "disclaimer-warranties",
    title: "2. No-Warranty Disclaimers on Website Content",
    icon: ShieldAlert,
    content: <>
          <p className="text-muted-foreground leading-relaxed">
            The information, images, project descriptions, articles, and general construction guidance presented on this website are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. While Brookhaven Elevation Renovations endeavors to maintain current and accurate details, we make no representations or warranties of any kind, express or implied, regarding the completeness, accuracy, reliability, or timeliness of any content.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            Remodeling timelines, material availability, and architectural considerations vary with every residence. Information on this site should not be substituted for direct on-site professional consultations, architectural evaluations, or municipal engineering reviews.
          </p>
        </>
  }, {
    id: "intellectual-property",
    title: "3. Intellectual Property and Content Ownership",
    icon: Copyright,
    content: <>
          <p className="text-muted-foreground leading-relaxed">
            All text, photographs, graphics, trademarks, logos, design layouts, and other media on this website are the property of Brookhaven Elevation Renovations or its respective licensors and are protected under United States and international copyright and trademark laws.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            You may not copy, reproduce, republish, download, post, broadcast, or transmit any site content for commercial exploitation without prior written consent from Brookhaven Elevation Renovations. Personal, non-commercial viewing of our portfolio materials is permitted.
          </p>
        </>
  }, {
    id: "estimates-contracts",
    title: "4. Estimates Are Non-Binding Until Executed Contract",
    icon: Calculator,
    content: <>
          <p className="text-muted-foreground leading-relaxed">
            Any preliminary pricing discussions, verbal ranges, online consultation guides, or written preliminary cost estimates provided through or generated from this site do not constitute a binding legal contract or a fixed-price commitment.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            A binding legal agreement for renovation, remodeling, additions, or construction work is created solely upon the mutual execution of a formal, written construction contract signed by both the homeowner and an authorized representative of Brookhaven Elevation Renovations, accompanied by agreed specifications, architectural drawings, and deposit schedules.
          </p>
        </>
  }, {
    id: "limitation-liability",
    title: "5. Limitation of Liability",
    icon: Scale,
    content: <>
          <p className="text-muted-foreground leading-relaxed">
            To the fullest extent permitted by applicable law, Brookhaven Elevation Renovations, its officers, managers, employees, and subcontractors shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of your access to, use of, or inability to use this website.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            This includes, without limitation, damages for errors, omissions, interruptions, computer viruses, or loss of data resulting from the browsing of this website or the downloading of any materials from it.
          </p>
        </>
  }, {
    id: "governing-law",
    title: "6. Governing Law and Jurisdiction",
    icon: Gavel,
    content: <>
          <p className="text-muted-foreground leading-relaxed">
            These Terms of Use and any claims or disputes arising out of or related to the use of this website shall be governed by and construed in accordance with the substantive laws of the State of Georgia, without regard to its conflict of law principles.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            You agree that any legal action or proceeding arising out of these Terms shall be instituted exclusively in the appropriate state or federal courts located in the State of Georgia.
          </p>
        </>
  }];
  return <section id="termsofuse" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono uppercase text-xs tracking-wider border-primary/30 text-primary">
            Legal Information
          </Badge>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Terms of Use
          </h1>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Please review these terms carefully before exploring our portfolio or requesting renovation estimates. These provisions outline website guidelines, intellectual property rights, non-binding estimates, and governing law.
          </p>
          <div className="flex items-center gap-2 mt-4 text-xs sm:text-sm text-muted-foreground font-mono">
            <Clock className="w-4 h-4 text-primary" />
            <span>Effective Date: Current &amp; Active</span>
          </div>
        </div>

        {/* Content Layout with Sidebar Navigation and Document Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Main Terms Document Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Visual Overview Feature */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-card shadow-sm">
              <div className="aspect-[16/6] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/architectural_contract_documentation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/architectural_contract_documentation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/architectural_contract_documentation.png-wvc-sizes" alt="Architectural renovation blueprints and signed legal documentation on a work desk" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="p-6 bg-card border-t border-border">
                <h2 className="text-xl font-serif font-medium text-foreground">
                  Brookhaven Elevation Renovations Terms Overview
                </h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  These terms govern your digital interaction with our company website and clarify the procedural steps between initial estimate requests and binding residential construction agreements in Georgia.
                </p>
              </div>
            </div>

            {/* Structured Sections */}
            <div className="space-y-6">
              {sections.map((section, idx) => {
              const IconComponent = section.icon;
              return <Card key={section.id} id={section.id} data-index={idx} className="border border-border/80 shadow-xs">
                    <CardHeader className="pb-3 border-b border-border/50">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-muted text-primary shrink-0">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <CardTitle className="text-lg md:text-xl font-serif font-medium tracking-tight">
                          {section.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4 text-sm md:text-base">
                      {section.content}
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>

          {/* Quick Sidebar Context & Contact */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            {/* Document Navigation */}
            <Card className="border border-border shadow-xs bg-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <FileText className="w-4 h-4 text-primary" />
                  <span>Document Index</span>
                </div>
                <CardDescription className="text-xs">
                  Direct links to each section of our Terms of Use
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <nav className="flex flex-col space-y-2 text-sm">
                  {sections.map((sec, i) => <Link key={sec.id} data-index={i} to={`#${sec.id}`} className="text-muted-foreground hover:text-foreground transition-colors py-1 px-2 rounded-md hover:bg-muted text-xs font-medium">
                      {sec.title}
                    </Link>)}
                </nav>
              </CardContent>
            </Card>

            {/* Legal Questions & Contact Point */}
            <Card className="border border-border/90 bg-muted/40 shadow-xs">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-serif font-semibold">
                  Inquiries &amp; Questions
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  For formal inquiries regarding these website terms or project scope discussions:
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0 space-y-4">
                <div className="p-3 bg-card rounded-md border border-border">
                  <span className="text-xs font-medium text-muted-foreground block mb-1">
                    Contact Point
                  </span>
                  <a href="mailto:serviceleadalerts@gmail.com" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors break-all">
                    <Mail className="w-4 h-4 text-primary shrink-0" />
                    <span>serviceleadalerts@gmail.com</span>
                  </a>
                </div>

                <div className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-3">
                  <p>
                    Brookhaven Elevation Renovations provides residential renovations in Brookhaven, GA and surrounding communities. Project contracts are separately executed in writing prior to any construction work.
                  </p>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>

        {/* Closing Note */}
        <Separator className="my-12" />
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Brookhaven Elevation Renovations. All rights reserved. Georgia-based residential construction and remodeling.
          </p>
        </div>
      </div>
    </section>;
}